import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProfile } from '../hooks/useProfile';
import { Title } from '../components/ui/Title';
import { Ear, Star, Plus, Users, MapPin, CreditCard as Edit, Music2, User } from 'lucide-react';
import type { Band, Event, FanSubscription } from '../types/database';
import { supabase } from '../lib/supabase';
import { ActionMenu } from '../components/ui/ActionMenu';
import { Grid } from '../components/ui/Grid';
import { BandPanel } from '../components/BandPanel';
import { EventPanel } from '../components/EventPanel';
import { Headline } from '../components/ui/Headline';
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';
import { useMusicTranslations } from '../utils/translations';
import { format } from 'date-fns';
import { dateLocales } from '../i18n';

type BandWithRole = Band & { role: string };
type FavoriteEvent = Event & { band: Band };
type FanSubscriptionWithBand = FanSubscription & { band: Band };

function Profile() {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { profile, loading, error } = useProfile();
  const { translateGenre, translateSkill, translateMusicianStatus } = useMusicTranslations();
  const [bands, setBands] = useState<BandWithRole[]>([]);
  const [favoriteBands, setFavoriteBands] = useState<Band[]>([]);
  const [favoriteEvents, setFavoriteEvents] = useState<FavoriteEvent[]>([]);
  const [fanSubscriptions, setFanSubscriptions] = useState<FanSubscriptionWithBand[]>([]);
  const [loadingBands, setLoadingBands] = useState(true);
  const [loadingFavorites, setLoadingFavorites] = useState(true);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const locale = dateLocales[i18n.language as keyof typeof dateLocales] || dateLocales.en;

  const profileActions = [
    {
      label: t('profile.editProfile'),
      icon: <Edit className="w-4 h-4" />,
      onClick: () => navigate('/profile/edit'),
    }
  ];

  useEffect(() => {
    async function fetchBands() {
      if (!profile) return;

      try {
        const { data: memberships, error: membershipError } = await supabase
          .from('band_members')
          .select(`
            role,
            band:bands (
              id,
              name,
              description,
              avatar_url,
              genres,
              location,
              slug
            )
          `)
          .eq('profile_id', profile.id);

        if (membershipError) throw membershipError;

        const bandsWithRoles = memberships?.map(m => ({
          ...m.band,
          role: m.role,
        })) || [];

        setBands(bandsWithRoles);
      } catch (err) {
        console.error('Error fetching bands:', err);
      } finally {
        setLoadingBands(false);
      }
    }

    async function fetchFavorites() {
      if (!profile) return;
      
      try {
        // Only fetch favorites for non-musicians
        if (!profile.is_musician) {
          // Fetch favorite bands
          const { data: favBands, error: favBandsError } = await supabase
            .from('favorite_bands')
            .select(`
              band:bands!favorite_bands_band_id_fkey(*)
            `)
            .eq('user_id', profile.id);

          if (favBandsError) throw favBandsError;

          setFavoriteBands(favBands?.map(fb => fb.band) || []);

          // Fetch favorite events
          const { data: favEvents, error: favEventsError } = await supabase
            .from('favorite_events')
            .select(`
              event:events!favorite_events_event_id_fkey(*)
            `)
            .eq('user_id', profile.id);

          if (favEventsError) throw favEventsError;

          const eventsWithBands = await Promise.all(
            (favEvents || []).map(async (fe) => {
              const { data: eventBand } = await supabase
                .from('event_bands')
                .select('band:bands!event_bands_band_id_fkey(*)')
                .eq('event_id', fe.event.id)
                .limit(1)
                .maybeSingle();

              return {
                ...fe.event,
                band: eventBand?.band || null
              };
            })
          );

          setFavoriteEvents(eventsWithBands);
        }
      } catch (err) {
        console.error('Error fetching favorites:', err);
      } finally {
        setLoadingFavorites(false);
      }
    }

    async function fetchFanSubscriptions() {
      if (!profile) return;
      
      try {
        // Fetch active fan subscriptions
        const { data: subscriptions, error: subscriptionsError } = await supabase
          .from('fan_subscriptions')
          .select(`
            *,
            band:bands!fan_subscriptions_band_id_fkey(*)
          `)
          .eq('user_id', profile.id)
          .in('status', ['completed', 'active']);

        if (subscriptionsError) throw subscriptionsError;

        setFanSubscriptions(subscriptions || []);
      } catch (err) {
        console.error('Error fetching fan subscriptions:', err);
      } finally {
        setLoadingSubscriptions(false);
      }
    }

    fetchBands();
    fetchFavorites();
    fetchFanSubscriptions();
  }, [profile]);

  if (loading) {
    return (
      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="text-center text-light/60">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="text-center text-red-500">
          Error loading profile: {error.message}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="text-center">
          <p className="text-light/60 mb-4">No profile found. Create one to continue.</p>
          <button
            onClick={() => navigate('/create-profile')}
            className="btn-primary"
          >
            Create Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 pb-6 md:py-24">
      <motion.div 
        className="max-w-4xl mx-auto"
        initial={{ opacity: 0, x: 200 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="panel mb-8 relative">
          <div className="absolute top-4 right-4 z-10">
            <ActionMenu items={profileActions} />
          </div>
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-100 sm:w-40 aspect-square rounded-full bg-gray-custom/10 flex items-center justify-center overflow-hidden">
              {profile.avatar_url ? (
                <img
                  src={profile.avatar_url}
                  alt={profile.artist_name}
                  className="w-full h-full object-cover"
                 loading="lazy" />
              ) : (
                <User className="w-12 h-12 text-light/60" />
              )}
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="text-3xl font-bold text-light mb-2">{profile.artist_name}</h1>
              {profile.full_name && (
                <p className="text-xl text-light/60 mb-2">{profile.full_name}</p>
              )}
              {profile.location && (
                <div className="flex items-center text-light/40">
                  <MapPin className="w-4 h-4 mr-1" />
                  <span>{profile.location}</span>
                </div>
              )}
            </div>
          </div>

          {profile.is_musician && (
            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-12 mt-6">
              {profile.genres && profile.genres.length > 0 && (
                <div>
                  <h2 className="font-semibold text-xl mb-3">
                    {t('musician.genres')}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {profile.genres.map((genre) => (
                      <span
                        key={genre}
                        className="px-3 py-1 bg-tertiary rounded-full text-light"
                      >
                        {translateGenre(genre)}
                      </span>
                    ))}
                  </div>
               </div>
              )}
              {profile.skills && profile.skills.length > 0 && (
                <div>
                  <h2 className="flex items-center text-xl font-semibold mb-3">
                    {t('musician.skills')}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill) => (
                      <span
                        key={skill}
                        className="px-3 py-1 bg-secondary rounded-full text-light"
                      >
                        {translateSkill(skill)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* TODO: Re-set this with actual musician status */}
              <div>
                <h2 className="font-semibold text-xl mb-3">
                  Status
                </h2>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-primary rounded-full text-light">{translateMusicianStatus(profile.status)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Profile Description Section */}
        {profile.description && (
          <div className="mb-8">
            <div>
              <div className="border-l border-solid border-primary pl-6 text-light/60 whitespace-pre-line">{profile.description}</div>
            </div>
          </div>
        )}

        {profile.is_musician && (
          <div>
            <div className="flex justify-between items-center mb-6 relative">
              <h2 className="text-2xl font-semibold">{t('profile.activeBands')}</h2>
              <button
                onClick={() => navigate('/bands/new')}
                className="btn-outline absolute right-0 flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span className="hidden md:inline">{t('profile.createBand')}</span>
              </button>
            </div>

            {loadingBands ? (
              <p className="text-light/60">{t('profile.loadingBands')}</p>
            ) : bands.length > 0 ? (
              <div className="space-y-6">
                {bands.map((band) => (
                  <Link
                    key={band.id}
                    to={`/bands/${band.slug}`}
                    className="block panel hover:bg-gray-custom/20"
                  >
                    <div className="flex items-center space-x-6">
                      <div className="w-32 h-32 bg-gray-custom/10 flex items-center justify-center overflow-hidden">
                        {band.avatar_url ? (
                          <img
                            src={band.avatar_url}
                            alt={band.name}
                            className="w-full h-full object-cover"
                           loading="lazy" />
                        ) : (
                          <Users className="w-8 h-8 text-light/60" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-light mb-2">{band.name}</h3>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-sm text-light/60">
                          <span>{band.role}</span>
                          {band.location && (
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              <span>{band.location}</span>
                            </div>
                          )}
                        </div>
                        {band.genres && band.genres.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {band.genres.map((genre) => (
                              <span
                                key={genre}
                                className="px-2 py-0.5 text-xs bg-tertiary rounded-full text-light"
                              >
                                {translateGenre(genre)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 panel">
                <p className="text-light/60 mb-4">{t('profile.noBandsYet')}</p>
                <Link to="/bands" className="block mt-4 underline">
                  {t('profile.browseBands')}
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Fan Support Section - Only show if user has active subscriptions */}
        {fanSubscriptions.length > 0 && !loadingSubscriptions && (
          <div className="my-8">
            <h2 className="text-2xl font-semibold mb-6">{t('fanArea.title')}</h2>
            <div className="space-y-6">
              {fanSubscriptions.map((subscription) => (
                <Link
                  key={subscription.id}
                  to={`/bands/${subscription.band.slug}`}
                  className="block panel hover:bg-gray-custom/20"
                >
                  <div className="flex items-center space-x-6">
                    <div className="w-32 h-32 bg-gray-custom/10 flex items-center justify-center overflow-hidden">
                      {subscription.band.avatar_url ? (
                        <img
                          src={subscription.band.avatar_url}
                          alt={subscription.band.name}
                          className="w-full h-full object-cover"
                         loading="lazy" />
                      ) : (
                        <Users className="w-8 h-8 text-light/60" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-light mb-2">{subscription.band.name}</h3>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-sm text-light/60">
                        <span>{t('fanArea.activeSubscription')} {format(new Date(subscription.payment_date), 'PPP', { locale })}</span>
                        {subscription.band.location && (
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            <span>{subscription.band.location}</span>
                          </div>
                        )}
                      </div>
                      {subscription.band.fan_area_price && (
                        <div className="text-sm text-light/60 mt-1">
                          {t('fanArea.support')}: {new Intl.NumberFormat('de-CH', {
                            style: 'currency',
                            currency: 'CHF',
                          }).format(subscription.amount_paid / 100)}
                        </div>
                      )}
                      {subscription.band.genres && subscription.band.genres.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {subscription.band.genres.map((genre) => (
                            <span
                              key={genre}
                              className="px-2 py-0.5 text-xs bg-tertiary rounded-full text-light"
                            >
                              {translateGenre(genre)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <Link
                        to={`/bands/${subscription.band.slug}/fan-content`}
                        className="btn-outline text-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('fanArea.fanContent')}
                      </Link>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Favorite Bands Section - Only for non-musicians */}
        {!profile.is_musician && (
          <div>
            <h2 className="text-2xl font-semibold mb-6">{t('favorites.favoriteBands')}</h2>
            {favoriteBands.length > 0 && !loadingFavorites ? (
              <Grid cols={3}>
                {favoriteBands.map((band) => (
                  <BandPanel key={band.id} band={band} />
                ))}
              </Grid>
            ) : (
              <div className="panel text-center py-6 text-light/60">{t('favorites.noFavorites')}</div>
            )}
          </div>
        )}

        {/* Favorite Events Section */}
        {!profile.is_musician && (
          <div className="mt-8">
            <h2 className="text-2xl font-semibold mb-6">{t('favorites.favoriteEvents')}</h2>
            {favoriteEvents.length > 0 && !loadingFavorites ? (
              <Grid cols={3}>
                {favoriteEvents.map((event) => (
                  <EventPanel 
                    key={event.id} 
                    event={event} 
                    bandSlug={event.band.slug}
                  />
                ))}
              </Grid>
            ) : (
              <div className="panel text-center py-6 text-light/60">{t('favorites.noFavorites')}</div>
            )}
          </div>
        )}
      </motion.div>

    </div>
  );
}

export default Profile;